import { IconButton } from "@/components/form"
import Icon from "@/components/Icon"
import { Operator, type Filter, type ObjectSchemaProperty } from "@/models"
import { faCircleInfo, faXmarkCircle } from "@fortawesome/free-solid-svg-icons"
import { css } from "vite-css-in-js"
import { computed } from "vue"
import { debounce, defineComponent, useOnInput, type ReactiveComponent } from "vue-utils"
import { useEditingSearchState } from "../../state"
import { getRequiredObjectSchemaProperty } from "../Helpers"

const filterFormatting = css`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	height: 0;
	flex-grow: 1;
	flex-shrink: 1;
	flex-basis: 0%;
`

const closeButtonStyles = css`
	color: var(--bs-secondary);
	border: none;
	padding: 0;
	margin: 0;
	outline: none;
	background: transparent;

	&:hover {
		color: #303030;
	}

	svg {
		height: 1.5rem;
	}
`

const QuickFilterProperties = [
	getRequiredObjectSchemaProperty(["Details", "LastName"]),
	getRequiredObjectSchemaProperty(["ClaimRef"]),
	getRequiredObjectSchemaProperty(["Details", "Email"]),
	getRequiredObjectSchemaProperty(["Details", "NINumber"]),
	getRequiredObjectSchemaProperty(["Address", "Postcode"]),
]

const SimpleFilters: ReactiveComponent = () => {
	const state = useEditingSearchState()
	const searchDebounced = debounce(() => void state.executeSearch(), 500)
	const otherFiltersPresent = computed<boolean>(
		() =>
			state.filters.filter((filter) => {
				return (
					QuickFilterProperties.filter((property) => {
						return property.equals(filter.Property)
					}).length === 0
				)
			}).length > 0
	)

	function matchesSimpleFilter(property: ObjectSchemaProperty, filter: Filter) {
		return (
			(filter.Operator === Operator.StringContains || filter.Operator == Operator.Equals) &&
			property.equals(filter.Property)
		)
	}

	function updateFilterValue(property: ObjectSchemaProperty, value: string) {
		if (value) {
			//Remove existing advanced filters for the property
			state.fullSearchOptions.Filters = state.filters.filter(
				(filter) => !filter.Property.equals(property) || matchesSimpleFilter(property, filter)
			)
		}
		const index = state.filters.findIndex((f) => matchesSimpleFilter(property, f))
		if (value && index < 0) {
			//Add filter
			state.filters.push({
				Operator: Operator.StringContains,
				Property: property,
				Value: value,
			})
		} else if (value && index >= 0) {
			//Edit filter value
			const filter = state.filters[index]
			filter.Value = value
		} else if (index >= 0) {
			//Remove filter
			state.filters.splice(index, 1)
		}
		state.saveFilters()
		searchDebounced()
	}

	function getFilterValue(property: ObjectSchemaProperty) {
		const filter = state.filters.find((f) => matchesSimpleFilter(property, f))
		return filter ? filter.Value : ""
	}

	function renderSimpleFilter(property: ObjectSchemaProperty) {
		const value = getFilterValue(property)
		return (
			<div key={property.vueKey()}>
				<h6>{`${property.formatName()}:`}</h6>
				<div class="d-flex gap-2 justify-content-between">
					<input
						class="flex-grow-1"
						type="text"
						value={value}
						onInput={useOnInput((newValue) => updateFilterValue(property, newValue))}
					/>
					{!!value && (
						<button
							class={closeButtonStyles}
							type="button"
							title="Clear LastName Filter"
							onClick={() => updateFilterValue(property, "")}
						>
							<Icon icon={faXmarkCircle} cursor="pointer" />
						</button>
					)}
				</div>
			</div>
		)
	}

	return () => (
		<div class={filterFormatting}>
			{QuickFilterProperties.map(renderSimpleFilter)}
			{otherFiltersPresent.value && (
				<IconButton color="dark" icon={faCircleInfo} disabled>
					Other Filters Are Present
				</IconButton>
			)}
		</div>
	)
}

export default defineComponent(SimpleFilters)
